import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Wind, ListTodo, Pill, Calendar, LogOut, Home } from "lucide-react";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import RoutinePage from "./pages/RoutinePage";
import MedicationPage from "./pages/MedicationPage";
import CalendarPage from "./pages/CalendarPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("emma_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (e) {
          localStorage.removeItem("emma_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (password) => {
    const response = await axios.post(`${API}/auth/login`, { password });
    const { token: newToken, ...userData } = response.data;
    localStorage.setItem("emma_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("emma_token");
    setToken(null);
    setUser(null);
  };

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, authHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFCFB] flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#A3E4D7] to-[#AED6F1] animate-pulse" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Simple Menu Button for Home - only logout
const HomeMenuButton = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  return (
    <button
      onClick={() => {
        logout();
        navigate("/login");
      }}
      data-testid="home-logout-button"
      className="fixed top-4 right-4 z-40 p-3 rounded-full bg-white/80 backdrop-blur-sm shadow-soft text-[#7F8C8D] hover:bg-white hover:text-[#2C3E50] transition-all"
      title="Déconnexion"
    >
      <LogOut size={20} />
    </button>
  );
};

// Navigation Component for other pages
const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const navItems = [
    { path: "/", icon: Home, label: "Accueil" },
    { path: "/routine", icon: ListTodo, label: "Routine" },
    { path: "/medicaments", icon: Pill, label: "Temesta" },
    { path: "/calendrier", icon: Calendar, label: "Historique" },
  ];

  return (
    <nav className="glass-header sticky top-0 z-50 px-4 py-3">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-nunito font-bold text-[#2C3E50] text-lg">
            Bonjour {user?.name} 
          </span>
        </div>
        
        <div className="flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`p-2 rounded-full transition-all duration-200 ${
                location.pathname === item.path 
                  ? "bg-[#A3E4D7] text-[#2C3E50]" 
                  : "text-[#7F8C8D] hover:bg-[#A3E4D7]/20"
              }`}
              title={item.label}
            >
              <item.icon size={20} />
            </button>
          ))}
          
          <button
            onClick={() => {
              logout();
              navigate("/login");
              toast.success("À bientôt !");
            }}
            data-testid="nav-logout"
            className="p-2 rounded-full text-[#7F8C8D] hover:bg-[#FAD7A1]/30 transition-all duration-200 ml-2"
            title="Déconnexion"
          >
            <LogOut size={20} />
          </button>
        </div>
      </div>
    </nav>
  );
};

// Home Layout (minimal, breathing focused)
const HomeLayout = ({ children }) => {
  return (
    <div className="app-container min-h-screen">
      <HomeMenuButton />
      <main className="max-w-4xl mx-auto px-4 py-4">
        {children}
      </main>
    </div>
  );
};

// Layout with Navigation for other pages
const AppLayout = ({ children }) => {
  return (
    <div className="app-container">
      <Navigation />
      <main className="max-w-4xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: '#FFFFFF',
              border: 'none',
              borderRadius: '1rem',
              boxShadow: '0 10px 40px rgba(174, 214, 241, 0.3)',
              color: '#2C3E50',
              fontFamily: 'Figtree, sans-serif',
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <HomeLayout><DashboardPage /></HomeLayout>
            </ProtectedRoute>
          } />
          <Route path="/routine" element={
            <ProtectedRoute>
              <AppLayout><RoutinePage /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/medicaments" element={
            <ProtectedRoute>
              <AppLayout><MedicationPage /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/calendrier" element={
            <ProtectedRoute>
              <AppLayout><CalendarPage /></AppLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
